import React, { Component } from 'react'
import styled from 'styled-components'
import Logic from './logic'

// Needed components
import EquipmentHeader from 'components/EquipmentHeader'
import Section from 'components/Section'
import EquipmentListItem from 'components/EquipmentListItem'
import PrimaryButton from 'components/PrimaryButton'
import Tip from 'components/Tip'

// Needed containers
import DivisionNavbar from 'containers/DivisionNavbar'
import EquipmentsModal from 'containers/EquipmentsModal'
import EquipmentsEditModal from 'containers/EquipmentsEditModal'

// Assets
import ElectricityIcon from './images/ic-eletricidade.svg'
import GasIcon from './images/ic-gas.svg'
import PlusIcon from './images/plus.svg'

// Utils
import findIndex from 'lodash/findIndex'

import { generateMediaUrl, findOfferType } from 'utils'

import { ELECTRICITY_OFFER_TYPE, GAS_OFFER_TYPE } from 'config'

/* eslint-disable camelcase */
@Logic
export default class Equipments extends Component {
  /**
   * Adds a new equipment
   */
  onAddEquipments = (equipments) => {
    const { toggleEquipmentsModal, setValues } = this.actions
    const merged = [
      ...new Set([...this.props.values.equipments, ...equipments]),
    ]

    const {
      defaultChargingDuration,
      defaultChargingPower,
      defaultChargingFrequency,
    } = this.props.data

    let carCharging = undefined
    if (equipments.find(({ svg_key }) => svg_key === 'el-carro')) {
      carCharging = {
        power: defaultChargingPower ? defaultChargingPower.value : 2300,
        frequency: defaultChargingFrequency ? defaultChargingFrequency.id : 1,
        duration: defaultChargingDuration ? defaultChargingDuration.value : 12,
      }
    }
    setValues({ equipments: merged, carCharging })
    toggleEquipmentsModal(null)
  }

  /**
   * Removes an equipment by id
   */
  removeEquipment = (idToRemove) => {
    const { setValues } = this.actions
    const { currentDivision, firstDivision } = this.props
    const selectedDivision = currentDivision || firstDivision

    let equipments = [...this.props.values.equipments]
    const index = findIndex(equipments, ({ id, house_divisions }) => {
      return house_divisions === selectedDivision.id && idToRemove === id
    })

    if (index !== -1) {
      const eqi = equipments[index]
      if (eqi.svg_key === 'el-carro') {
        setValues({ carCharging: undefined })
      }

      equipments.splice(index, 1)
      setValues({ equipments })
    }
  }

  /**
   * Opens Edition Modal
   */
  editEquipment = (equipment) => {
    const { toggleEquipmentsEditModal } = this.actions
    toggleEquipmentsEditModal(Object.assign(equipment))
  }

  /**
   * Saves edited equipment
   */
  saveEquipment = (equipment) => {
    const { toggleEquipmentsEditModal, setValues } = this.actions
    const { currentDivision, firstDivision } = this.props
    const selectedDivision = currentDivision || firstDivision

    // Create a independent object
    let equipments = [...this.props.values.equipments]
    let index = findIndex(
      equipments,
      ({ id, house_divisions }) =>
        equipment.id === id && house_divisions === selectedDivision.id
    )

    // Set selected class and quantity
    equipments[index].selectedClass = equipment.selectedClass
    equipments[index].quantity = equipment.quantity
    setValues({ equipments })
    toggleEquipmentsEditModal(false)
  }

  render() {
    const {
      setDivision,
      toggleEquipmentsModal,
      toggleEquipmentsEditModal,
      openEquipmentsList,
    } = this.actions

    const {
      divisions,
      firstDivision,
      currentDivision,
      offerType,
      offerTypes,
      equipments,
      equipmentsOfferType,
      equipmentsOpened,
      modalOpened,
      showEquipmentsList,
      translations,
    } = this.props

    const { HOUSE_EQUIPMENTS } = translations || {}

    const selectedDivision = currentDivision || firstDivision

    const selectedEquipments =
      this.props.values.equipments &&
      this.props.values.equipments.filter(
        ({ house_divisions }) => house_divisions === selectedDivision.id
      )

    // Check if gas or electricity or both
    const isGas = offerType && offerType.slug.indexOf(GAS_OFFER_TYPE) !== -1

    const isElectricity =
      offerType && offerType.slug.indexOf(ELECTRICITY_OFFER_TYPE) !== -1

    const isFullOne = !(isGas && isElectricity)

    // Filters equipments by offer type
    const gasEquipments =
      selectedEquipments &&
      selectedEquipments
        .filter(({ offer_type }) => {
          let { slug } = findOfferType(offer_type, offerTypes)
          return slug === GAS_OFFER_TYPE
        })
        .filter(({ house_divisions }) => {
          return house_divisions === selectedDivision.id
        })

    const electricEquipments =
      selectedEquipments &&
      selectedEquipments
        .filter(({ offer_type }) => {
          let { slug } = findOfferType(offer_type, offerTypes)
          return slug === ELECTRICITY_OFFER_TYPE
        })
        .filter(({ house_divisions }) => {
          return house_divisions === selectedDivision.id
        })

    // Configure edit modal props
    // https://gitlab.ubiwhere.com/adene-poupa-energia/adene-simulador-avancado/issues/21
    const equipmentsOfferTypeObject = findOfferType(
      equipmentsOfferType,
      offerTypes
    )
    // console.log(equipmentsOfferType)
    const equipmentsModalProps = {
      equipments,
      toggleEquipmentsModal,
      selectedEquipments,
      offerType: equipmentsOfferType,
      offerTypeName:
        equipmentsOfferTypeObject && equipmentsOfferTypeObject.name,
      selectedDivision,
    }

    // Map equipment classes to all classes
    let equipmentClasses = []
    if (modalOpened) {
      equipmentClasses = [...modalOpened.classes]

      equipmentClasses = equipmentClasses.map((item) => {
        item.label = item.classe_energetica.name
        return item
      })
    }

    return (
      <StyledSection
        title={`Equipamentos da Habitação`}
        subtitle={`Caracterize os equipamentos da sua habitação`}>
        {HOUSE_EQUIPMENTS && <Tip data-slot='tip' text={HOUSE_EQUIPMENTS} />}
        <Container>
          {/* Show disclaimer if list is closed */}
          {!showEquipmentsList && (
            <Disclaimer>
              Definimos um conjunto de equipamentos para a sua casa. <br />
              Se pretender alterá-los selecione aqui
              <br />
              <DisclaimerButton
                text={`Alterar Equipamentos`}
                onClick={openEquipmentsList}
              />
            </Disclaimer>
          )}

          {/* Show list if opened */}
          {showEquipmentsList && (
            <div>
              <EquipmentHeader
                icon={
                  selectedDivision &&
                  selectedDivision.icon &&
                  generateMediaUrl(selectedDivision.icon)
                }
                title={selectedDivision && selectedDivision.name}
              />

              <ContentWrapper>
                {isElectricity && (
                  <Column full={isFullOne}>
                    <ColumnHeader
                      icon={ElectricityIcon}
                      title={`Equipamentos de consumo de Eletricidade`}
                    />

                    {electricEquipments && !!electricEquipments.length && (
                      <ColumnHeadings>
                        <span>Tipo de equipamento</span>
                        <span>Classe Energética</span>
                      </ColumnHeadings>
                    )}

                    <ColumnContent>
                      {electricEquipments &&
                        electricEquipments.map(
                          ({ house_divisions, ...item }, i) => {
                            return (
                              <EquipmentListItem
                                {...item}
                                key={`eqe-${i}`}
                                onRemove={() => this.removeEquipment(item.id)}
                                onEdit={() => this.editEquipment(item)}
                                showEnergyClass={item.svg_key !== 'el-carro'}
                              />
                            )
                          }
                        )}
                    </ColumnContent>

                    <AddEquipment
                      empty={electricEquipments && !electricEquipments.length}
                      onClick={() => toggleEquipmentsModal(2)}>
                      Adicionar Equipamento
                    </AddEquipment>
                  </Column>
                )}

                {isGas && (
                  <Column full={isFullOne}>
                    <ColumnHeader
                      icon={GasIcon}
                      title={`Equipamentos de consumo de Gás Natural`}
                    />

                    {gasEquipments && !!gasEquipments.length && (
                      <ColumnHeadings>
                        <span>Tipo de equipamento</span>
                        <span>Classe Energética</span>
                      </ColumnHeadings>
                    )}

                    <ColumnContent>
                      {gasEquipments &&
                        gasEquipments.map((item, i) => (
                          <EquipmentListItem
                            {...item}
                            key={`eqg-${i}`}
                            onRemove={() => this.removeEquipment(item.id)}
                            onEdit={() => this.editEquipment(item)}
                            showEnergyClass={item.svg_key !== 'el-carro'}
                          />
                        ))}
                    </ColumnContent>

                    <AddEquipment
                      onClick={() => toggleEquipmentsModal(3)}
                      empty={gasEquipments && !gasEquipments.length}>
                      Adicionar Equipamento
                    </AddEquipment>
                  </Column>
                )}
              </ContentWrapper>

              <DivisionNavbar
                divisions={divisions}
                selected={selectedDivision}
                onClick={(division) => setDivision(division)}
              />
            </div>
          )}
        </Container>

        {/* Edit modal */}
        {modalOpened && (
          <EquipmentsEditModal
            toggle={toggleEquipmentsEditModal}
            energyClasses={equipmentClasses}
            equipment={modalOpened}
            saveEquipment={this.saveEquipment}
          />
        )}

        {/* Equipments modal */}
        {equipmentsOpened && (
          <EquipmentsModal
            {...equipmentsModalProps}
            onAddEquipments={this.onAddEquipments}
            toggle={toggleEquipmentsModal}
          />
        )}
      </StyledSection>
    )
  }
}

const StyledSection = styled(Section)`
  padding: 0;
  padding-top: 80px;
`
const Container = styled.div`
  display: block;
  position: relative;
  margin: 0;
  padding: 0;
  margin-top: 50px;
  padding-bottom: 80px;
  text-align: center;
`
const ContentWrapper = styled.div`
  display: flex;
  max-width: 930px;
  height: auto;
  margin: 0 auto;
  padding: 0;
  margin-top: 50px;
  padding-bottom: 140px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`
const Column = styled.div`
  flex: 0.5;
  text-align: left;
  display: flex;
  flex-direction: column;

  @media (max-width: 600px) {
    flex: 1;
  }

  &:first-child {
    margin-right: 40px;

    @media (max-width: 600px) {
      margin-right: 0;
    }
  }

  &:before,
  &:after {
    content: '';
    display: table;
  }

  &:after {
    clear: both;
  }
  zoom: 1;

  ${({ full }) => (full ? `margin: 0 auto!important;` : '')};
`
const ColumnHeader = styled(EquipmentHeader)`
  text-align: left;
  width: 100%;
  margin-bottom: 20px;

  > span {
    line-height: 20px;
    text-transform: uppercase;
    font-size: 16px;
  }

  @media (max-width: 860px) {
    display: flex;
    padding: 0 8px;
  }
`
const ColumnHeadings = styled.div`
  display: inline-flex;

  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;

  /* width: 440px; */

  > span {
    display: inline-block;
    text-align: left;
    color: #676767;
    font-family: Nunito;
    font-size: 12px;
    line-height: 12px;
    flex: 1 1 auto;
  }
`
const ColumnContent = styled.ul`
  list-style: none;
  width: 100%;
  margin: 0;
  padding: 0;
`
const AddEquipment = styled.button`
  border: 1px solid #dedede;
  color: #3e3e3e;
  font-size: 12px;
  font-weight: bold;
  line-height: 12px;
  padding: 10px;
  margin: 10px 0;
  margin-left: auto;
  background: none;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  background-image: url(${PlusIcon});
  background-size: 20px 20px;
  background-position: 95%;
  padding-right: 35px;
  background-repeat: no-repeat;

  &:hover {
    color: #93c13e;
  }

  ${({ empty }) =>
    empty &&
    `

  margin: 40px auto;

  `}
`

const Disclaimer = styled.div`
  display: block;
  font-size: 18px;
  color: #222;
`

const DisclaimerButton = styled(PrimaryButton)`
  display: block;
  max-width: 250px;
  margin: 20px auto;
`



// WEBPACK FOOTER //
// ./src/containers/Equipments/index.js